import { useContext, useEffect, useMemo } from "react";
import {
  ConversationContext,
  ConversationDetailsContext,
  useEmailReply
} from "../../context";
import { ConversationTypeEnum } from "@coxauto-ui/cxm/communications/interfaces";
import {
  SelectInput,
  SelectInputEventValue
} from "@interstate/components/SelectInput";
import { conversationChannelSelectorId } from "../../testIds";
import { InterstateOnChangeEvent } from "@interstate/components/InterstateEvents";
import {
  getToChannelValueSelectInputOptions,
  useComponentWidth
} from "@coxauto-ui/cxm/communications/util";
import styled from "styled-components";
import classNames from "classnames";

export interface EmailChannelValueSelectorProps {
  isDisabled: boolean;
}

const StyledChannelValueSelector = styled.div`
  overflow: hidden;
  width: 100%;

  &.large {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;

    &.email-only {
      > div {
        flex: 1;
        overflow: hidden;
      }
    }
  }
`;

export function EmailChannelValueSelector({
  isDisabled
}: EmailChannelValueSelectorProps) {
  const [width, ref] = useComponentWidth();

  // ✅ Use email-specific context hook
  const { selectedToChannelValue, setSelectedToChannelValue } = useEmailReply();

  const { recentContactEmail } = useContext(ConversationDetailsContext);
  const { contactDetails } = useContext(ConversationContext);

  useEffect(() => {
    if (recentContactEmail && recentContactEmail !== selectedToChannelValue) {
      setSelectedToChannelValue(recentContactEmail);
    }
    // Note: selectedToChannelValue intentionally NOT in dependencies to avoid
    // overriding user selections when they change the dropdown value
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentContactEmail, setSelectedToChannelValue]);

  const toChannelOptions = useMemo(
    () =>
      getToChannelValueSelectInputOptions(
        contactDetails,
        ConversationTypeEnum.EMAIL
      ),
    [contactDetails]
  );

  const onChannelSelection = (
    e: InterstateOnChangeEvent<SelectInputEventValue>
  ) => {
    const value = e.target.value?.toString();
    if (value === undefined) return;
    if (value !== selectedToChannelValue) {
      setSelectedToChannelValue(value);
    }
  };

  return (
    <StyledChannelValueSelector
      className={classNames({
        "email-only": true,
        large: width > 600
      })}
      ref={ref}
    >
      <SelectInput
        data-testid={conversationChannelSelectorId}
        disabled={isDisabled}
        displayDeselectOption={false}
        displayLabel={false}
        id="conversation-channel-pane-dropdown"
        name="ConversationChannelValueDropdown"
        options={toChannelOptions}
        value={selectedToChannelValue}
        onChange={onChannelSelection}
      />
    </StyledChannelValueSelector>
  );
}

export default EmailChannelValueSelector;
